import React, { useEffect, useState } from 'react';
import InfoDialog from '../../../../components/shared/InfoDialog';
import { Box, List, ListItemButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../store';
import CusInput from '../../../../components/material-ui/CusInput';
import CusCheckbox from '../../../../components/material-ui/CusCheckbox';
import CusButton from '../../../../components/material-ui/CusButton';
import { API_USERS } from '../../../../api/users';
import BalanceAside from './BalanceAside';
import CurrencyValue from '../../../../components/material-ui/CurrencyValue';
import BalanceBoldTypography from './BalanceBoldTypography';
import LabelCheckbox from './LabelCheckbox';
import DepositAdd from './DepositAdd';
import ShowContainer from '../../../../components/material-ui/ShowContainer';
import useToast from '../../../../hooks/useToast';
import { TEST_MODE } from '../../../../enums/enums';

const plugCommon = {
  RUB: {},
  EUR: {},
  KZT: {},
  KGS: {},
  UZS: {},
  ARS: {},
  BRL: {},
  USDT: {}
};

const plugDetail = {
  depositBalance: [],
  bonusBalance: []
};

export default function ChangeBalance({ open, closeModal, user }) {
  const { t } = useTranslation();
  const { successToast } = useToast();

  const [currencyInfo, setCurrencyInfo] = useState({});
  const [commonBalance, setCommonBalance] = useState({});
  const [detailBalance, setDetailBalance] = useState({});
  const [selectDeposits, setSelectDeposits] = useState([]);
  const [selectBonuses, setSelectBonuses] = useState([]);
  const [checkOpenForPayment, setCheckOpenForPayment] = useState(false);

  const title = `${t('Changing user balance')} ${user?.login}`;
  const userId = user.userId;
  const currencies = Object.keys(commonBalance);

  const selectCurrency = currencyInfo.currency || 'RUB';
  const currencyId = currencyInfo.userId;
  const openBalance = commonBalance[selectCurrency]?.main;
  const depositBalance = commonBalance[selectCurrency]?.deposit;
  const bonusBalance = commonBalance[selectCurrency]?.bonus;
  const mainBalance = openBalance + depositBalance;

  const deposits = detailBalance?.depositBalance || [];
  const bonuses = detailBalance?.bonusBalance || [];

  const updateCommon = () => {
    if (TEST_MODE) {
      setCommonBalance({
        RUB: { main: 4228.4, deposit: 295604, bonus: 500 },
        EUR: { main: 0, deposit: 10050, bonus: 0 },
        KZT: { main: 0, deposit: 0, bonus: 0 },
        KGS: { main: 0, deposit: 0, bonus: 0 },
        UZS: { main: 0, deposit: 0, bonus: 0 },
        AZN: { main: 0, deposit: 0, bonus: 0 },
        ARS: { main: 0, deposit: 0, bonus: 0 },
        BRL: { main: 0, deposit: 0, bonus: 0 },
        USDT: { main: 0, deposit: 0, bonus: 0 }
      });
      return;
    }
    API_USERS.info.balances
      .list({ userId })
      .then((e) => {
        const list = e.data.list;
        setCommonBalance(list);
      })
      .catch(console.error);
  };

  const updateDetails = () => {
    if (TEST_MODE) {
      setDetailBalance({
        mainBalance: 4228.4,
        depositBalance: [
          { id: 176, balance: 1000, wager: 2, remained: 2000 },
          { id: 175, balance: 150, wager: 5, remained: 750 },
          { id: 94, balance: 294454, wager: 40, remained: 252732 }
        ],
        bonusBalance: [{ id: 126, balance: 500, wager: 2, remained: 2000 }]
      });
      return;
    }
    API_USERS.info.balances
      .detail({ userId, currency: selectCurrency })
      .then((e) => {
        const detail = e.data.list;
        setDetailBalance(detail);
      })
      .catch(console.error);
  };

  const updateInfo = () => {
    updateCommon();
    updateDetails();
  };

  useEffect(() => {
    if (!open) {
      setCommonBalance(plugCommon);
      setDetailBalance(plugDetail);
      return;
    }
    if (!userId) return;

    setCurrencyInfo({ currency: user.currency, userId });

    updateCommon();
  }, [userId, open]);

  useEffect(() => {
    if (!userId) return;
    if (!open) return;
    if (currencyId !== userId) return;

    updateDetails();
  }, [userId, open, selectCurrency, currencyId]);

  const setSelectCurrency = (val) => setCurrencyInfo((prev) => ({ ...prev, currency: val }));

  const deleteMarked = () => {
    // console.log(selectDeposits, selectBonuses);

    // const depBalances = selectDeposits
    //   .map((id) => deposits.find((el) => el.id == id).balance)
    //   .reduce((prev, cur) => prev + cur, 0);

    // const bonusBalances = selectBonuses
    //   .map((id) => bonuses.find((el) => el.id == id).balance)
    //   .reduce((prev, cur) => prev + cur, 0);

    // console.log('depBalances', depBalances, bonusBalances);
    // const newDeps = deposits.filter((el) => !selectDeposits.includes(el.id));
    // const newBonuses = bonuses.filter((el) => !selectDeposits.includes(el.id));

    // setDetailBalance((prev) => ({ ...prev, depositBalance: newDeps, bonusBalance: newBonuses }));
    // setCommonBalance((prev) => ({
    //   ...prev,
    //   [selectCurrency]: {
    //     main: checkOpenForPayment ? 0 : prev[selectCurrency].main,
    //     deposit: prev[selectCurrency].deposit - depBalances,
    //     bonus: prev[selectCurrency].bonus - bonusBalances
    //   }
    // }));

    API_USERS.info.balances
      .clear({
        userId,
        currency: selectCurrency,
        depositList: selectDeposits,
        bonusList: selectBonuses,
        mainBalance: checkOpenForPayment
      })
      .then((e) => {
        successToast('Successfully');
        updateInfo();
        setSelectDeposits([]);
        setSelectBonuses([]);
      })
      .catch(console.error);
  };

  return (
    <InfoDialog open={open} onClose={closeModal} title={title} maxWidth="md">
      <Stack direction={'row'} gap={4} mt={2} width={'100%'}>
        <BalanceAside
          commonBalance={commonBalance}
          currencies={currencies}
          selectCurrency={selectCurrency}
          setSelectCurrency={setSelectCurrency}
        />

        <Stack gap={3} width={'100%'}>
          <Stack
            gap={0.75}
            width={'100%'}
            sx={{ backgroundColor: (theme) => theme.palette.grey[50], p: 1, borderRadius: 1 }}>
            <Stack direction={'row'} width={'100%'} alignItems={'center'} gap={1}>
              <BalanceBoldTypography fontSize={18}>{t('Main balance')}:</BalanceBoldTypography>
              <CurrencyValue value={mainBalance} currency={selectCurrency} />
            </Stack>
            <Stack gap={0.5}>
              <BalanceBoldTypography color="textSecondary" fontSize={14} text={'consists of'} />
              <CusCheckbox
                checked={checkOpenForPayment}
                onChange={() => setCheckOpenForPayment((prev) => !prev)}
                label={
                  <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                    <BalanceBoldTypography>{t('Open for payment')}:</BalanceBoldTypography>{' '}
                    <CurrencyValue value={openBalance} currency={selectCurrency} />
                  </Stack>
                }
              />
            </Stack>

            <Stack width={'100%'} pl={4.5}>
              <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                <BalanceBoldTypography>{t('Deposit balance')}:</BalanceBoldTypography>{' '}
                <CurrencyValue value={depositBalance} currency={selectCurrency} />
              </Stack>
            </Stack>

            <ShowContainer condition={!!deposits.length}>
              <Stack gap={0} pl={4.5}>
                <BalanceBoldTypography color="textSecondary" fontSize={14} text={'consists of'} />
                {deposits.map((el, indx) => (
                  <CusCheckbox
                    key={indx}
                    checked={selectDeposits.includes(el.id)}
                    onChange={() =>
                      setSelectDeposits((prev) =>
                        prev.includes(el.id)
                          ? prev.filter((prevEl) => prevEl != el.id)
                          : [...prev, el.id]
                      )
                    }
                    label={
                      <LabelCheckbox
                        currencyValue={el.balance}
                        currency={selectCurrency}
                        wager={el.wager}
                        remained={el.remained}
                      />
                    }
                  />
                ))}
              </Stack>
            </ShowContainer>
          </Stack>

          <Stack width={'100%'} px={1}>
            <Stack direction={'row'} gap={0.5} alignItems={'center'}>
              <BalanceBoldTypography>{t('Bonus balance')}:</BalanceBoldTypography>{' '}
              <CurrencyValue value={bonusBalance} currency={selectCurrency} />
            </Stack>
            <ShowContainer condition={!!bonuses.length}>
              <Stack gap={0}>
                <BalanceBoldTypography color="textSecondary" fontSize={14} text={'consists of'} />

                {bonuses.map((el, indx) => (
                  <CusCheckbox
                    key={indx}
                    checked={selectBonuses.includes(el.id)}
                    onChange={() =>
                      setSelectBonuses((prev) =>
                        prev.includes(el.id)
                          ? prev.filter((prevEl) => prevEl != el.id)
                          : [...prev, el.id]
                      )
                    }
                    label={
                      <LabelCheckbox
                        currencyValue={el.balance}
                        currency={selectCurrency}
                        wager={el.wager}
                        remained={el.remained}
                      />
                    }
                  />
                ))}
              </Stack>
            </ShowContainer>
          </Stack>

          <ShowContainer condition={deposits.length || bonuses.length || openBalance}>
            <CusButton
              boxProps={{ px: 1 }}
              text="Remove marked ones"
              variant="outlined"
              onClick={deleteMarked}
              disabled={!(selectDeposits.length || selectBonuses.length || checkOpenForPayment)}
            />
          </ShowContainer>
        </Stack>
      </Stack>

      <DepositAdd userId={userId} currency={selectCurrency} updateInfo={updateInfo} />
    </InfoDialog>
  );
}
